import { Loader2, AlertCircle } from "lucide-react";
import { Button } from "../ui/Button";
import { formatPrice, formatFriendlyDate } from "../../lib/utils";
import { TEXT_CONTENT } from "../../lib/text-content";
import { useUser } from "../../lib/api-client";

interface RegistrationFooterProps {
  totalCents: number;
  currency: string;
  totalLabel?: string;
  bookingOpen: boolean;
  availablePricePointsLength: number;
  isFullyBooked?: boolean;
  bookingClosesAt?: string | null;
  isSubmitting: boolean;
  onAddAndContinue: () => void;
  onAddAndCheckout: () => void;
}

export function RegistrationFooter({
  totalCents,
  currency,
  totalLabel = "Total",
  bookingOpen,
  availablePricePointsLength,
  isFullyBooked = false,
  bookingClosesAt,
  isSubmitting,
  onAddAndContinue,
  onAddAndCheckout,
}: RegistrationFooterProps) {
  const { isAuthenticated } = useUser();

  return (
    <div className="pt-4 border-t">
      <div className="flex justify-between items-center mb-4">
        <span className="text-lg font-semibold">{totalLabel}</span>
        <span className="text-lg font-semibold">
          {formatPrice(totalCents, currency)}
        </span>
      </div>

      {!bookingOpen && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800 font-medium">
            {TEXT_CONTENT.EVENTS.BOOKING_CLOSED_TITLE}
          </p>
          {bookingClosesAt && (
            <p className="text-xs text-red-600 mt-1">
              {TEXT_CONTENT.EVENTS.BOOKING_CLOSED_PREFIX}{" "}
              {formatFriendlyDate(bookingClosesAt)}
            </p>
          )}
        </div>
      )}

      {bookingOpen && availablePricePointsLength === 0 && (
        <div
          className={
            isFullyBooked
              ? "mb-4 p-3 bg-red-50 border border-red-200 rounded-md"
              : "mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md"
          }
        >
          {isFullyBooked ? (
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
              <p className="text-sm font-medium text-red-800">
                {TEXT_CONTENT.EVENTS.EVENT_FULLY_BOOKED}
              </p>
            </div>
          ) : (
            <p className="text-sm text-yellow-800">
              {TEXT_CONTENT.EVENTS.NO_PRICE_POINTS}
            </p>
          )}
        </div>
      )}

      <div className="space-y-2">
        {isAuthenticated && (
          <Button
            className="w-full"
            variant="outline"
            onClick={onAddAndContinue}
            disabled={
              !bookingOpen ||
              availablePricePointsLength === 0 ||
              isSubmitting
            }
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {TEXT_CONTENT.EVENTS.ADD_TO_BASKET_LOADING}
              </>
            ) : (
              TEXT_CONTENT.EVENTS.ADD_TO_BASKET_AND_CONTINUE
            )}
          </Button>
        )}

        <Button
          className="w-full"
          onClick={onAddAndCheckout}
          disabled={
            !bookingOpen ||
            availablePricePointsLength === 0 ||
            isSubmitting
          }
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {TEXT_CONTENT.EVENTS.ADD_TO_BASKET_LOADING}
            </>
          ) : bookingOpen ? (
            TEXT_CONTENT.EVENTS.ADD_TO_BASKET_AND_VIEW
          ) : (
            TEXT_CONTENT.EVENTS.BOOKING_CLOSED
          )}
        </Button>
      </div>

      {bookingOpen && (
        <p className="text-sm text-gray-500 mt-2">
          {bookingClosesAt ? (
            <>
              {TEXT_CONTENT.EVENTS.BOOKINGS_CLOSE_PREFIX}{" "}
              {formatFriendlyDate(bookingClosesAt)}
            </>
          ) : (
            <>{TEXT_CONTENT.EVENTS.LIMITED_PLACES}</>
          )}
        </p>
      )}
    </div>
  );
}
